import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Select, Space, Spin, Table, Tag, Tooltip, Typography } from "antd";
import { showError } from "common/ToastNotifications";
import AppPaths from "constants/appPaths";
import moment from "moment";
import { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import actions from "store/actions";

const { Text } = Typography;

class MyTasks extends Component {
    constructor(props) {
        super(props);
        this.state = {
            busy: false,
            busyMessage: "",
            searchTerm: "",
            filters: {
                type: undefined,
                state: undefined,
                priority: undefined,
                closed: false,
                sortOrder: "desc",
            },
        };
    }

    noWrapTextStyle = {
        display: "inline-block",
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    };

    formatDisplayValue = (value) => {
        if (value === null || value === undefined) {
            return "-";
        }
        if (typeof value === "string" && value.trim() === "") {
            return "-";
        }
        return value;
    };

    componentDidMount() {
        this.showBreadCrumbNavigationItems();
        this.loadMyAnnotations();
    }

    componentDidUpdate(prevProps) {
        this.onGetMyAnnotationsResultUpdate(prevProps);
    }

    showBreadCrumbNavigationItems = () => {
        if (this.props.setBreadcrumbNavigationItems) {
            const breadCrumbItems = [
                {
                    route: "",
                    text: "My Assigned Annotations",
                },
            ];
            this.props.setBreadcrumbNavigationItems(breadCrumbItems);
        }
    };

    loadMyAnnotations = () => {
        this.setState({
            busy: true,
            busyMessage: "Loading your assigned annotations...",
        });
        this.props.getMyAnnotations(this.state.filters);
    };

    onGetMyAnnotationsResultUpdate(prevProps) {
        const { getMyAnnotationsResult } = this.props;
        if (getMyAnnotationsResult !== prevProps.getMyAnnotationsResult) {
            if (getMyAnnotationsResult?.loading) {
                return;
            }

            this.setState({ busy: false, busyMessage: "" });
            if (getMyAnnotationsResult?.error) {
                showError("Failed to load annotations");
            }
        }
    }

    handleFilterChange = (key, value) => {
        this.setState(
            (prevState) => ({
                filters: {
                    ...prevState.filters,
                    [key]: value,
                },
            }),
            () => {
                this.loadMyAnnotations();
            },
        );
    };

    handleSearch = (e) => {
        this.setState({ searchTerm: e.target.value });
    };

    getTypeColor = (type) => {
        switch (type) {
            case "issue":
                return "red";
            case "task":
                return "blue";
            case "comment":
                return "green";
            default:
                return "default";
        }
    };

    getStateColor = (state) => {
        switch (state) {
            case "open":
                return "blue";
            case "in_progress":
                return "orange";
            case "resolved":
                return "green";
            case "dismissed":
                return "gray";
            default:
                return "default";
        }
    };

    getPriorityColor = (priority) => {
        switch (priority) {
            case "high":
                return "red";
            case "medium":
                return "orange";
            case "low":
                return "green";
            default:
                return "default";
        }
    };

    getRecordTypeLabel = (recordType) => {
        switch (recordType) {
            case "schema":
                return "Schema";
            case "schemaField":
            case "schema_attribute":
                return "Schema Field";
            case "dataset":
                return "Dataset";
            case "dataset_record":
                return "Dataset Record";
            case "dataRecord":
            case "datasetrecord":
                return "Data Object";
            default:
                return recordType;
        }
    };

    getAnnotationRedirectUrl = (annotation) => {
        if (!annotation) {
            return null;
        }
        return annotation.redirect_url || annotation.redirectUrl || null;
    };

    navigateToUrl = (url) => {
        const trimmedUrl = typeof url === "string" ? url.trim() : "";
        if (!trimmedUrl) {
            showError("Unable to determine navigation path");
            return;
        }

        try {
            const isAbsolute = /^https?:\/\//i.test(trimmedUrl);
            if (isAbsolute) {
                if (typeof window !== "undefined") {
                    window.location.assign(trimmedUrl);
                } else {
                    this.props.history.push(trimmedUrl);
                }
            } else {
                this.props.history.push(trimmedUrl.startsWith("/") ? trimmedUrl : `/${trimmedUrl}`);
            }
        } catch (error) {
            console.error("Error navigating via redirect URL:", error);
            showError("Failed to navigate to annotation context");
        }
    };

    handleJumpTo = (annotation) => {
        const redirectUrl = this.getAnnotationRedirectUrl(annotation);
        if (redirectUrl) {
            this.navigateToUrl(redirectUrl);
            return;
        }

        // TODO: annotation jump
        const { tenant, history } = this.props;
        const { recordType, recordId, businessAreaId } = annotation;

        if (!tenant || !tenant.name) {
            showError("Tenant information not available");
            return;
        }

        let targetUrl = null;

        try {
            switch (recordType) {
                case "schema":
                    targetUrl = AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", tenant.name)
                        .replace(":businessarea", businessAreaId || "default")
                        .replace(":schemaId", recordId)
                        .replace(":schemaAction", "view");
                    break;

                case "schema_attribute":
                    const schemaId = annotation.metadata?.schemaId || recordId.split(":")[0];
                    targetUrl = AppPaths.TENANT_BUSINESS_AREA_SCHEMA_ID_ACTION.replace(":tenant", tenant.name)
                        .replace(":businessarea", businessAreaId || "default")
                        .replace(":schemaId", schemaId)
                        .replace(":schemaAction", "view");
                    break;

                case "dataset":
                    const datasetSchemaId = annotation.metadata?.schemaId || annotation.schemaId;
                    if (datasetSchemaId) {
                        targetUrl = AppPaths.TENANT_SCHEMA_DATASET_ID_ACTION.replace(":tenant", tenant.name)
                            .replace(":businessarea", businessAreaId || "default")
                            .replace(":schema", datasetSchemaId)
                            .replace(":dataSetId", recordId)
                            .replace(":dataSetAction", "overview");
                    }
                    break;

                case "dataset_record":
                    const recordSchemaId = annotation.metadata?.schemaId || annotation.schemaId;
                    const datasetId = annotation.metadata?.datasetId || annotation.datasetId;
                    if (recordSchemaId && datasetId) {
                        targetUrl = AppPaths.TENANT_DATASET_DATAOBJECT_ID_ACTION.replace(":tenant", tenant.name)
                            .replace(":businessarea", businessAreaId || "default")
                            .replace(":schema", recordSchemaId)
                            .replace(":dataset", datasetId)
                            .replace(":dataObjectId", recordId)
                            .replace(":dataObjectAction", "view");
                    }
                    break;

                default:
                    showError(`Unknown record type: ${recordType}`);
                    return;
            }

            if (targetUrl) {
                history.push(targetUrl);
            } else {
                showError("Unable to determine navigation path");
            }
        } catch (error) {
            console.error("Error navigating to annotation:", error);
            showError("Failed to navigate to annotation context");
        }
    };

    getFilteredAnnotations = () => {
        const { myAnnotations } = this.props;
        const { searchTerm } = this.state;

        if (!myAnnotations || myAnnotations.length === 0) {
            return [];
        }

        const searchLower = searchTerm.toLowerCase().trim();
        if (!searchLower) {
            return myAnnotations;
        }

        return myAnnotations.filter((annotation) => {
            const content = (annotation.content || "").toLowerCase();
            const targetContext = (annotation.targetContext || "").toLowerCase();
            const targetName = (annotation.targetName || "").toLowerCase();
            const recordType = this.getRecordTypeLabel(annotation.recordType).toLowerCase();

            return content.includes(searchLower) || targetContext.includes(searchLower) || targetName.includes(searchLower) || recordType.includes(searchLower);
        });
    };

    render() {
        const { busy, busyMessage, filters, searchTerm } = this.state;

        const filteredAnnotations = this.getFilteredAnnotations();

        const columns = [
            {
                title: "ID",
                dataIndex: "annotationId",
                key: "annotationId",
                width: 180,
                render: (id) => {
                    const displayId = this.formatDisplayValue(id);
                    return (
                        <Tooltip title={displayId === "-" ? undefined : displayId}>
                            <Text code style={{ ...this.noWrapTextStyle, textOverflow: "clip" }}>
                                {displayId}
                            </Text>
                        </Tooltip>
                    );
                },
            },
            {
                title: "Priority",
                dataIndex: "priority",
                key: "priority",
                width: 50,
                ellipsis: true,
                render: (priority) => {
                    if (!priority) {
                        return (
                            <Text type="secondary" style={this.noWrapTextStyle}>
                                -
                            </Text>
                        );
                    }
                    return (
                        <Tag color={this.getPriorityColor(priority)} style={{ textTransform: "capitalize", whiteSpace: "nowrap" }}>
                            {priority}
                        </Tag>
                    );
                },
            },
            {
                title: "Type",
                dataIndex: "type",
                key: "type",
                width: 50,
                ellipsis: true,
                render: (type) => {
                    if (!type) {
                        return (
                            <Text type="secondary" style={this.noWrapTextStyle}>
                                -
                            </Text>
                        );
                    }
                    const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
                    return (
                        <Tag color={this.getTypeColor(type)} style={{ whiteSpace: "nowrap" }}>
                            {formattedType}
                        </Tag>
                    );
                },
            },
            {
                title: "Record Type",
                dataIndex: "recordType",
                key: "recordType",
                width: 60,
                ellipsis: true,
                render: (recordType, record) => {
                    const recordTypeLabel = this.formatDisplayValue(this.getRecordTypeLabel(recordType));
                    const metaLines = [];
                    const pushIfValue = (value) => {
                        if (typeof value === "string" && value.trim() !== "") {
                            metaLines.push(value);
                        }
                    };
                    pushIfValue(record.targetContext);
                    pushIfValue(record.targetName);

                    return (
                        <div>
                            <div>
                                <Text strong style={this.noWrapTextStyle}>
                                    {recordTypeLabel}
                                </Text>
                            </div>
                        </div>
                    );
                },
            },
            {
                title: "Field Name",
                dataIndex: "targetName",
                key: "targetName",
                width: 80,
                ellipsis: true,
                render: (targetName) => {
                    const displayName = this.formatDisplayValue(targetName);
                    return (
                        <Tooltip title={displayName === "-" ? undefined : displayName}>
                            <Text style={this.noWrapTextStyle}>{displayName}</Text>
                        </Tooltip>
                    );
                },
            },
            {
                title: "Content",
                dataIndex: "content",
                key: "content",
                width: 500,
                ellipsis: true,
                render: (content) => {
                    const displayContent = this.formatDisplayValue(content);
                    return (
                        <Tooltip title={displayContent === "-" ? undefined : displayContent}>
                            <Text style={this.noWrapTextStyle}>{displayContent}</Text>
                        </Tooltip>
                    );
                },
            },
            {
                title: "Status",
                dataIndex: "state",
                key: "state",
                width: 60,
                ellipsis: true,
                render: (state, record) => {
                    if (record.closed) {
                        return (
                            <Tag color="default" style={{ whiteSpace: "nowrap" }}>
                                Closed
                            </Tag>
                        );
                    }
                    if (!state) {
                        return (
                            <Text type="secondary" style={this.noWrapTextStyle}>
                                -
                            </Text>
                        );
                    }
                    return (
                        <Tag color={this.getStateColor(state)} style={{ textTransform: "capitalize", whiteSpace: "nowrap" }}>
                            {state.replace("_", " ")}
                        </Tag>
                    );
                },
            },
            {
                title: "Sustained",
                dataIndex: "sustained",
                key: "sustained",
                width: 60,
                ellipsis: true,
                render: (sustained) => {
                    if (sustained === true) {
                        return (
                            <Tag color="green" style={{ whiteSpace: "nowrap" }}>
                                Yes
                            </Tag>
                        );
                    }
                    if (sustained === false) {
                        return (
                            <Tag color="default" style={{ whiteSpace: "nowrap" }}>
                                No
                            </Tag>
                        );
                    }
                    return (
                        <Text type="secondary" style={this.noWrapTextStyle}>
                            -
                        </Text>
                    );
                },
            },
            {
                title: "Date Created",
                dataIndex: "createdDate",
                key: "createdDate",
                width: 80,
                ellipsis: true,
                render: (date) => {
                    const momentDate = date ? moment(date) : null;
                    if (!momentDate || !momentDate.isValid()) {
                        return (
                            <Text type="secondary" style={this.noWrapTextStyle}>
                                -
                            </Text>
                        );
                    }
                    return (
                        <Tooltip title={momentDate.format("YYYY-MM-DD HH:mm:ss")}>
                            <Text type="secondary" style={this.noWrapTextStyle}>
                                {momentDate.fromNow()}
                            </Text>
                        </Tooltip>
                    );
                },
            },
            {
                title: "Action",
                key: "action",
                width: 50,
                fixed: "right",
                render: (_, record) => {
                    const hasRedirectUrl = Boolean(this.getAnnotationRedirectUrl(record));
                    return (
                        <Button type="link" size="small" onClick={() => this.handleJumpTo(record)} disabled={!hasRedirectUrl}>
                            Jump to →
                        </Button>
                    );
                },
            },
        ];

        return (
            <Spin spinning={busy} tip={busyMessage}>
                <div style={{ padding: "0px" }}>
                    <div style={{ marginBottom: 16 }}>
                        <Space orientation="vertical" style={{ width: "100%" }} size="middle">
                            <Space wrap>
                                <Input
                                    placeholder="Search annotations..."
                                    prefix={<SearchOutlined />}
                                    value={searchTerm}
                                    onChange={this.handleSearch}
                                    style={{ width: 300 }}
                                    allowClear
                                />
                                <Select
                                    placeholder="Filter by Type"
                                    style={{ width: 150 }}
                                    value={filters.type}
                                    onChange={(value) => this.handleFilterChange("type", value)}
                                    allowClear
                                >
                                    <Select.Option value="task">Task</Select.Option>
                                    <Select.Option value="issue">Issue</Select.Option>
                                    <Select.Option value="comment">Comment</Select.Option>
                                </Select>
                                <Select
                                    placeholder="Filter by Status"
                                    style={{ width: 150 }}
                                    value={filters.state}
                                    onChange={(value) => this.handleFilterChange("state", value)}
                                    allowClear
                                >
                                    <Select.Option value="open">Open</Select.Option>
                                    <Select.Option value="in_progress">In Progress</Select.Option>
                                    <Select.Option value="resolved">Resolved</Select.Option>
                                    <Select.Option value="dismissed">Dismissed</Select.Option>
                                </Select>
                                <Select
                                    placeholder="Filter by Priority"
                                    style={{ width: 150 }}
                                    value={filters.priority}
                                    onChange={(value) => this.handleFilterChange("priority", value)}
                                    allowClear
                                >
                                    <Select.Option value="high">High</Select.Option>
                                    <Select.Option value="medium">Medium</Select.Option>
                                    <Select.Option value="low">Low</Select.Option>
                                </Select>
                                <Select placeholder="Include Closed" style={{ width: 150 }} value={filters.closed} onChange={(value) => this.handleFilterChange("closed", value)}>
                                    <Select.Option value={false}>Open Only</Select.Option>
                                    <Select.Option value={true}>Include Closed</Select.Option>
                                </Select>
                                <Select placeholder="Sort Order" style={{ width: 150 }} value={filters.sortOrder} onChange={(value) => this.handleFilterChange("sortOrder", value)}>
                                    <Select.Option value="desc">Newest First</Select.Option>
                                    <Select.Option value="asc">Oldest First</Select.Option>
                                </Select>
                                <Button icon={<ReloadOutlined />} onClick={this.loadMyAnnotations}>
                                    Refresh
                                </Button>
                            </Space>
                        </Space>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={filteredAnnotations}
                        rowKey="annotationId"
                        pagination={{
                            pageSize: 20,
                            showTotal: (total) => `Total ${total} annotations`,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                        }}
                        locale={{
                            emptyText: (
                                <Empty description={searchTerm ? "No annotations match your search" : "No annotations assigned to you"}>
                                    {!searchTerm && <Text type="secondary">Annotations assigned to you will appear here</Text>}
                                </Empty>
                            ),
                        }}
                        scroll={{ x: 1200 }}
                    />
                </div>
            </Spin>
        );
    }
}

const mapStateToProps = (state) => ({
    tenant: state.userLogin.tenant,
    user: state.userLogin.user,
    myAnnotations: state.annotations.myAnnotations || [],
    getMyAnnotationsResult: state.annotations.getMyAnnotationsResult,
});

const mapDispatchToProps = (dispatch) => ({
    getMyAnnotations: (filters) => dispatch(actions.getMyAnnotationsRequest(filters)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MyTasks));
